import React from "react";
import { alpha } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import AppBar from "@mui/material/AppBar";
import { Box, Typography, Toolbar, Hidden as MuiHidden } from "@mui/material";
import { Done, Close } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { PropTypes } from "prop-types";
import { useTheme } from "@mui/styles";
import ButtonGroup from "../Buttons/ButtonGroup";

const useStyles = makeStyles(theme => ({
  appBar: {
    top: "auto",
    bottom: 0,
    zIndex: 1,
    height: 100,
    padding: theme.spacing(3),
    paddingLeft: theme.sizes.sideNavBarWidthCondensed,
    boxShadow: "none",
    borderTop: `solid 1px ${theme.palette.divider}`,
    backgroundColor: theme.palette.common.white,
    [theme.breakpoints.down("sm")]: {
      paddingLeft: theme.spacing(3)
    }
  },
  toolbar: {
    padding: theme.spacing(0, 3.5, 0, 6.5),
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      padding: 0
    }
  },
  answerBox: {
    padding: theme.spacing(0.5, 0.5, 0.5, 1),
    border: `solid 1px ${theme.palette.secondary[200]}`,
    minWidth: 46,
    height: 52,
    marginRight: 5,
    backgroundColor: "none"
  },
  answerBoxHasAnswer: {
    backgroundColor: theme.palette.action.hover
  },
  answerBoxCorrect: {
    backgroundColor: alpha(theme.palette.success.main, 0.04)
  },
  answerBoxIncorrect: {
    backgroundColor: alpha(theme.palette.error.main, 0.04)
  },
  checkmark: {
    alignSelf: "flex-end"
  },
  questionsSavedContainer: {
    "& > *": {
      paddingRight: theme.spacing(0.5)
    }
  },
  responsesContainer: {
    overflow: "auto"
  }
}));

/**
 * The box that contains the question number and a checkmark if the question has a response
 * @param {boolean} response - True if the current answer does not equal {} because it has a response
 * @param {number} questionNumber - The question number
 */
const ResponseBox = ({ response, questionNumber, showCorrectAnswer }) => {
  const classes = useStyles();
  const { palette } = useTheme();
  let indicatorColor = palette.action.active;
  if (showCorrectAnswer) {
    indicatorColor = response ? palette.success.main : palette.error.main;
  }
  return (
    <Box
      className={clsx(classes.answerBox, {
        [classes.answerBoxHasAnswer]: response && !showCorrectAnswer,
        [classes.answerBoxCorrect]: response && showCorrectAnswer,
        [classes.answerBoxIncorrect]: !response && showCorrectAnswer
      })}
      display="flex"
      flexDirection="column"
      data-qa="response-tracker-box"
    >
      <Typography variant="caption" color="primary">
        {questionNumber}
      </Typography>
      <Box color={indicatorColor}>
        {response && (
          <Done
            className={classes.checkmark}
            fontSize="small"
            color="inherit"
            data-qa="response-tracker-box-checkmark"
          />
        )}
        {showCorrectAnswer && !response && (
          <Close
            className={classes.checkmark}
            fontSize="small"
            color="inherit"
            data-qa="response-tracker-box-incorrect"
          />
        )}
      </Box>
    </Box>
  );
};

const QuestionsSaved = ({
  totalQuestions,
  numResponses,
  showCorrectAnswers
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  return (
    <Box
      display="flex"
      pl={{ xs: 0, sm: 3.5 }}
      alignItems="flex-end"
      className={classes.questionsSavedContainer}
      fontSize={{ xs: "1.5rem", sm: "1rem" }}
    >
      <Typography color="textPrimary" variant="inherit">
        {numResponses}
      </Typography>
      <Typography color="textSecondary" variant="inherit">
        {"/"}
      </Typography>
      <Typography color="textPrimary" variant="inherit">
        {totalQuestions}
      </Typography>
      <Box pl={0.5}>
        <Typography variant="caption" color="textSecondary" noWrap>
          {showCorrectAnswers
            ? t("courseQuiz.responseTracker.correctResponses")
            : t("courseQuiz.responseTracker.questionsAnswered")}
        </Typography>
      </Box>
    </Box>
  );
};

export default function ResponseTracker(props) {
  const {
    answeredQuestions,
    navigationButtons,
    showCorrectAnswers,
    quizActionButtons
  } = props;
  const classes = useStyles();
  const totalQuestions = answeredQuestions ? answeredQuestions.length : 0;
  const numResponses = answeredQuestions
    ? answeredQuestions.filter(answeredQuestion => answeredQuestion).length
    : 0;

  return (
    <React.Fragment>
      <AppBar position="fixed" color="default" className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          <Box
            display="flex"
            alignItems="center"
            overflow="auto"
            flex={1}
            justifyContent={"flex-start"}
          >
            <Box
              display={{ xs: "none", sm: "flex" }}
              className={classes.responsesContainer}
            >
              {answeredQuestions.map((response, index) => (
                <ResponseBox
                  showCorrectAnswer={showCorrectAnswers}
                  key={index}
                  questionNumber={index + 1}
                  response={response}
                />
              ))}
            </Box>
            <QuestionsSaved
              totalQuestions={totalQuestions}
              numResponses={numResponses}
              showCorrectAnswers={showCorrectAnswers}
            />
          </Box>
          <Box ml={3}>
            <ButtonGroup>
              {quizActionButtons}
              <MuiHidden smDown>{navigationButtons}</MuiHidden>
            </ButtonGroup>
          </Box>
        </Toolbar>
      </AppBar>
    </React.Fragment>
  );
}

ResponseTracker.propTypes = {
  /** An array of booleans that will be true if the question has been answered, false otherwise
   * If the quiz has been submitted, answeredQuestions will be an array of booleans that will be true if the question was answered correctly, false otherwise.
   **/
  answeredQuestions: PropTypes.arrayOf(PropTypes.bool),
  showCorrectAnswers: PropTypes.bool
};
